// Analytics Utilities for Portfolio Website
import { format } from 'date-fns';

// Analytics data structure
const defaultAnalytics = {
  pageViews: {},
  buttonClicks: {},
  blogInteractions: {},
  sessionData: {},
  userEngagement: {},
  lastUpdated: new Date().toISOString()
};

// Get analytics data from localStorage
export const getAnalyticsData = () => {
  try {
    const data = localStorage.getItem('portfolioAnalytics');
    return data ? JSON.parse(data) : defaultAnalytics;
  } catch (error) {
    console.error('Error loading analytics data:', error);
    return defaultAnalytics;
  }
};

// Save analytics data to localStorage
export const saveAnalyticsData = (data) => {
  try {
    localStorage.setItem('portfolioAnalytics', JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Error saving analytics data:', error);
    return false;
  }
};

// Track page view
export const trackPageView = (pageName) => {
  const analytics = getAnalyticsData();
  const today = format(new Date(), 'yyyy-MM-dd');
  
  if (!analytics.pageViews[pageName]) {
    analytics.pageViews[pageName] = {};
  }
  
  if (!analytics.pageViews[pageName][today]) {
    analytics.pageViews[pageName][today] = 0;
  }
  
  analytics.pageViews[pageName][today]++;
  analytics.lastUpdated = new Date().toISOString();
  
  saveAnalyticsData(analytics);
};

// Track button click
export const trackButtonClick = (buttonName, pageName = 'unknown') => {
  const analytics = getAnalyticsData();
  const today = format(new Date(), 'yyyy-MM-dd');
  
  if (!analytics.buttonClicks[buttonName]) {
    analytics.buttonClicks[buttonName] = {
      totalClicks: 0,
      dailyClicks: {},
      pageContext: {}
    };
  }
  
  analytics.buttonClicks[buttonName].totalClicks++;
  
  if (!analytics.buttonClicks[buttonName].dailyClicks[today]) {
    analytics.buttonClicks[buttonName].dailyClicks[today] = 0;
  }
  analytics.buttonClicks[buttonName].dailyClicks[today]++;
  
  if (!analytics.buttonClicks[buttonName].pageContext[pageName]) {
    analytics.buttonClicks[buttonName].pageContext[pageName] = 0;
  }
  analytics.buttonClicks[buttonName].pageContext[pageName]++;
  
  analytics.lastUpdated = new Date().toISOString();
  saveAnalyticsData(analytics);
};

// Track blog post interaction (like, view)
export const trackBlogInteraction = (postId, action, postTitle = '') => {
  const analytics = getAnalyticsData();
  const today = format(new Date(), 'yyyy-MM-dd');
  
  if (!analytics.blogInteractions[postId]) {
    analytics.blogInteractions[postId] = {
      title: postTitle,
      views: 0,
      likes: 0,
      dailyViews: {},
      dailyLikes: {},
      lastViewed: null
    };
  }
  
  if (action === 'view') {
    analytics.blogInteractions[postId].views++;
    if (!analytics.blogInteractions[postId].dailyViews[today]) {
      analytics.blogInteractions[postId].dailyViews[today] = 0;
    }
    analytics.blogInteractions[postId].dailyViews[today]++;
    analytics.blogInteractions[postId].lastViewed = new Date().toISOString();
  } else if (action === 'like') {
    analytics.blogInteractions[postId].likes++;
    if (!analytics.blogInteractions[postId].dailyLikes[today]) {
      analytics.blogInteractions[postId].dailyLikes[today] = 0;
    }
    analytics.blogInteractions[postId].dailyLikes[today]++;
  }
  
  analytics.lastUpdated = new Date().toISOString();
  saveAnalyticsData(analytics);
};

// Track session data
export const trackSessionStart = () => {
  const analytics = getAnalyticsData();
  const sessionId = Date.now().toString();
  const today = format(new Date(), 'yyyy-MM-dd');
  
  if (!analytics.sessionData[today]) {
    analytics.sessionData[today] = [];
  }
  
  analytics.sessionData[today].push({
    sessionId,
    startTime: new Date().toISOString(),
    endTime: null,
    pagesVisited: [],
    totalTime: 0
  });
  
  analytics.lastUpdated = new Date().toISOString();
  saveAnalyticsData(analytics);
  return sessionId;
};

// Track page visit in session
export const trackPageVisit = (sessionId, pageName) => {
  const analytics = getAnalyticsData();
  const today = format(new Date(), 'yyyy-MM-dd');
  
  if (analytics.sessionData[today]) {
    const session = analytics.sessionData[today].find(s => s.sessionId === sessionId);
    if (session) {
      session.pagesVisited.push({
        page: pageName,
        timestamp: new Date().toISOString()
      });
      analytics.lastUpdated = new Date().toISOString();
      saveAnalyticsData(analytics);
    }
  }
};

// Track user engagement (time spent, scroll depth, etc.)
export const trackUserEngagement = (pageName, engagementType, value) => {
  const analytics = getAnalyticsData();
  const today = format(new Date(), 'yyyy-MM-dd');
  
  if (!analytics.userEngagement[pageName]) {
    analytics.userEngagement[pageName] = {};
  }
  
  if (!analytics.userEngagement[pageName][engagementType]) {
    analytics.userEngagement[pageName][engagementType] = {
      total: 0,
      daily: {},
      average: 0,
      count: 0
    };
  }
  
  const engagement = analytics.userEngagement[pageName][engagementType];
  engagement.total += value;
  engagement.count++;
  engagement.average = engagement.total / engagement.count;
  
  if (!engagement.daily[today]) {
    engagement.daily[today] = 0;
  }
  engagement.daily[today] += value;
  
  analytics.lastUpdated = new Date().toISOString();
  saveAnalyticsData(analytics);
};

// Get analytics summary for dashboard
export const getAnalyticsSummary = () => {
  const analytics = getAnalyticsData();
  const today = format(new Date(), 'yyyy-MM-dd');
  const last7Days = [];
  
  for (let i = 6; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    last7Days.push(format(date, 'yyyy-MM-dd'));
  }
  
  // Calculate total page views
  const totalPageViews = Object.values(analytics.pageViews).reduce((total, page) => {
    return total + Object.values(page).reduce((sum, count) => sum + count, 0);
  }, 0);
  
  // Calculate today's page views
  const todayPageViews = Object.values(analytics.pageViews).reduce((total, page) => {
    return total + (page[today] || 0);
  }, 0);
  
  // Calculate total button clicks
  const totalButtonClicks = Object.values(analytics.buttonClicks).reduce((total, button) => {
    return total + button.totalClicks;
  }, 0);
  
  // Calculate today's button clicks
  const todayButtonClicks = Object.values(analytics.buttonClicks).reduce((total, button) => {
    return total + (button.dailyClicks[today] || 0);
  }, 0);
  
  // Calculate blog statistics
  const totalBlogViews = Object.values(analytics.blogInteractions).reduce((total, post) => {
    return total + post.views;
  }, 0);
  
  const totalBlogLikes = Object.values(analytics.blogInteractions).reduce((total, post) => {
    return total + post.likes;
  }, 0);
  
  // Get most popular pages
  const pageViewsArray = Object.entries(analytics.pageViews).map(([page, dates]) => ({
    page,
    totalViews: Object.values(dates).reduce((sum, count) => sum + count, 0)
  })).sort((a, b) => b.totalViews - a.totalViews);
  
  // Get most clicked buttons
  const buttonClicksArray = Object.entries(analytics.buttonClicks).map(([button, data]) => ({
    button,
    totalClicks: data.totalClicks
  })).sort((a, b) => b.totalClicks - a.totalClicks);
  
  // Get most popular blog posts
  const blogPostsArray = Object.entries(analytics.blogInteractions).map(([id, data]) => ({
    id,
    title: data.title,
    views: data.views,
    likes: data.likes
  })).sort((a, b) => b.views - a.views);
  
  return {
    totalPageViews,
    todayPageViews,
    totalButtonClicks,
    todayButtonClicks,
    totalBlogViews,
    totalBlogLikes,
    popularPages: pageViewsArray.slice(0, 5),
    popularButtons: buttonClicksArray.slice(0, 5),
    popularPosts: blogPostsArray.slice(0, 5),
    last7Days,
    lastUpdated: analytics.lastUpdated
  };
};

// Export analytics data as JSON
export const exportAnalyticsData = () => {
  const analytics = getAnalyticsData();
  const dataStr = JSON.stringify(analytics, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `portfolio-analytics-${format(new Date(), 'yyyy-MM-dd-HH-mm')}.json`;
  link.click();
  URL.revokeObjectURL(url);
};

// Clear analytics data
export const clearAnalyticsData = () => {
  localStorage.removeItem('portfolioAnalytics');
  return true;
}; 